import React, {useEffect, useRef, useState} from 'react';
import {Linking, Platform} from 'react-native';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../../redux';
import {esiaLinkSelector, esiaUserNotAcceptSelector} from '../../../redux/selectors/login';
import {LoginState} from '../../../shared/models/login/loginStep';
import View from './view';
import {WebViewNavigation} from 'react-native-webview';
import {loginSlice} from '../../../redux/reducers/login';
import querystring from 'query-string';
import {loginStepSlice} from '../../../redux/reducers/runtime/loginStep';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import {esiaLogin} from '../../../redux/actions/login';
import {ShouldStartLoadRequest} from 'react-native-webview/lib/WebViewTypes';
import {checkEsiaRedirectUrl} from '../../../shared/utils/checkEsiaRedirectUrl';

const ESIA_HOW_TO_PAGE = 'www.gosuslugi.ru/help/faq/login/70000002';

export const Esia = () => {
  const dispatch = useAppDispatch();
  const [isProcessed, setIsProcessed] = useState(false);
  const processingRef = useRef(false);
  const redirectedRef = useRef(false);

  const link = useSelector(esiaLinkSelector);
  const isNotAccept = useSelector(esiaUserNotAcceptSelector);

  // Desktop + adaptive web: leave for ESIA. Do not reset login step here — that aborted the redirect.
  useEffect(() => {
    if (Platform.OS !== 'web' || isNotAccept || !link || redirectedRef.current) {
      return;
    }
    redirectedRef.current = true;
    window.location.assign(link);
  }, [isNotAccept, link]);

  if (Platform.OS === 'web' && !isNotAccept) {
    return null;
  }

  const goBack = () => {
    dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
    dispatch(loginSlice.actions.resetEsiaFlow(null));
  };

  const openHowToPage = async () => {
    if (Platform.OS === 'web') {
      window.open(ESIA_HOW_TO_PAGE, '_blank');
    } else {
      const supported = await Linking.canOpenURL(ESIA_HOW_TO_PAGE);
      if (supported) {
        await Linking.openURL(ESIA_HOW_TO_PAGE);
      }
    }
  };

  const isHandledOAuthRedirect = (url: string): boolean => {
    if (!url || processingRef.current) return false;

    const isRedirectToOurDomain = checkEsiaRedirectUrl(url);
    if (!isRedirectToOurDomain) return false;

    const [, params = ''] = url.split('?');
    const {code, state} = querystring.parse(params);
    if (!code || !state) return false;

    processingRef.current = true;
    dispatch(loginSlice.actions.setEsiaCode({code, state}));
    setIsProcessed(true);

    void Promise.resolve(dispatch(esiaLogin()))
      .catch(() => {})
      .finally(() => {
        processingRef.current = false;
        setIsProcessed(false);
      });

    return true;
  };

  const onNavigationStateChange = ({url}: WebViewNavigation) => {
    if (!url) return;
    if (!isHandledOAuthRedirect(url) && !processingRef.current) {
      const isRedirectToOurDomain = checkEsiaRedirectUrl(url);
      if (isRedirectToOurDomain && url.includes('?')) {
        goBack();
        dispatch(
          addNotification({
            message: 'Не удалось получить код авторизации ЕСИА',
            type: SnackbarNotificationType.Error,
          }),
        );
      }
    }
  };

  const onShouldStartLoadWithRequest = (request: ShouldStartLoadRequest) => {
    const url = request?.url;
    if (!url) return true;

    if (processingRef.current && checkEsiaRedirectUrl(url)) {
      return false;
    }

    return !isHandledOAuthRedirect(url);
  };

  return (
    <View
      link={link}
      isNotAccept={isNotAccept}
      isProcessed={isProcessed}
      goBack={goBack}
      openHowToPage={openHowToPage}
      onNavigationStateChange={onNavigationStateChange}
      onShouldStartLoadWithRequest={onShouldStartLoadWithRequest}
    />
  );
};
